import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import {
  usePostAgreementAdd,
  usePatchAgreementEdit,
  type AgreementAddRequestBodySchema,
} from '@repo/api/endpoints/agreement';
import { areP24MarketingDataAccepted, getP24MarketingAgreements } from '@repo/store/helpers';
import { useProviderStore } from '@repo/store/provider';
import some from 'lodash/some';

import { Agreements } from '@/components/agreements/agreements';
import { P24MarketingAgreementsFormInputs } from '@/components/agreements/p24-marketing-agreements-form-inputs';
import { marketingAgreementsDataToSend } from '@/components/welcome/components/p24-marketing-agreements-form/form/marketing-agreements-data-to-send';

import { pragmaAgreements } from './pragma-agreements';

interface LoanAgreements {
  merchantResponseData: MerchantResponse;
  // Registers the marketing-consent submit so the host form can fire it synchronously
  // on submit. A post-submit effect is unreliable here: the modal host unmounts this
  // component right after submit, before any effect would run.
  onRegisterMarketingSubmit?: (submit: () => void) => void;
}

export function LoanAgreements({
  merchantResponseData,
  onRegisterMarketingSubmit,
}: LoanAgreements) {
  const { getValues } = useFormContext();
  const {
    state: { currentProvider },
  } = useProviderStore();
  const { mutate: postAgreementAdd } = usePostAgreementAdd();
  const { mutate: patchAgreementEdit } = usePatchAgreementEdit();

  const p24marketingAgreements = getP24MarketingAgreements(
    merchantResponseData.data.agreements?.marketing,
  );
  const displayMarketingAgreements = !areP24MarketingDataAccepted(p24marketingAgreements);

  // Persist the P24 marketing consents (email/sms/tel) like the welcome flow. They live
  // in the loan form's RHF state but are stripped by the resolver, so read via getValues.
  const submitMarketingAgreements = () => {
    if (!displayMarketingAgreements || !currentProvider) return;

    const data = {
      email: Boolean(getValues('email')),
      sms: Boolean(getValues('sms')),
      tel: Boolean(getValues('tel')),
    };

    const payload: AgreementAddRequestBodySchema = {
      requestId: merchantResponseData.requestId,
      apiProvider: 'pragma',
      agreements: {
        marketing: marketingAgreementsDataToSend(data, merchantResponseData),
      },
    };
    if (some(data, (value) => value)) {
      if (p24marketingAgreements && p24marketingAgreements.length > 0) {
        patchAgreementEdit(payload);
      } else {
        postAgreementAdd(payload);
      }
    }
  };

  // Keep the latest closure in a ref and register a stable wrapper once, so the host can
  // invoke it synchronously during submit (before it potentially unmounts this component).
  const submitMarketingRef = useRef(submitMarketingAgreements);
  submitMarketingRef.current = submitMarketingAgreements;

  useEffect(() => {
    onRegisterMarketingSubmit?.(() => submitMarketingRef.current());
  }, [onRegisterMarketingSubmit]);

  return (
    <>
      <Agreements
        merchantResponseData={merchantResponseData}
        agreements={pragmaAgreements}
        includeP24MarketingAgreements={displayMarketingAgreements}
      />
      {displayMarketingAgreements ? (
        <Box marginTop={1} marginLeft="7px">
          <P24MarketingAgreementsFormInputs />
        </Box>
      ) : null}
    </>
  );
}
